'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSaveFeaturedBadges } from '@/lib/api/rewards-hub/hooks';
import { type Badges, FEATURED_BADGE_LIMIT, type RewardBadge } from '@/lib/api/rewards-hub/models';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/lib/ui-kit/dialog';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { BadgeMedallion } from './BadgeMedallion';
import { focusDialogOnOpen } from './dialogFocus';

/**
 * "My achievements" — the pin board behind the profile's collection card
 * (Figma 13449:480677). Every owned medallion is laid out in a wrap grid with
 * a pin toggle in its corner; the four the player pins lead their profile.
 *
 * Nothing is written until Save, so the dialog holds its own draft and throws
 * it away when it closes on Close.
 */

interface FeatureAchievementsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  badges: Badges | undefined;
}

/** Owned badges, newest award first — the order the pin board reads in. */
export function ownedBadges(badges: Badges | undefined): RewardBadge[] {
  return (badges?.items ?? [])
    .filter((badge) => badge.owned)
    .sort((a, b) => (b.awardedAt?.getTime() ?? 0) - (a.awardedAt?.getTime() ?? 0));
}

interface PinnableBadgeProps {
  badge: RewardBadge;
  pinned: boolean;
  /** A full board stops taking pins until one is let go. */
  disabled: boolean;
  onToggle: (badgeId: string) => void;
}

function PinnableBadge({ badge, pinned, disabled, onToggle }: PinnableBadgeProps) {
  const t = useTranslations('rewardsHub.achievements');
  const handleClick = useCallback(() => onToggle(badge.id), [badge.id, onToggle]);

  return (
    <li className="relative size-[72px] shrink-0">
      {/* Pinned art sits in a bordered well; unpinned art stands on its own. */}
      <span
        className={cn(
          'block size-full rounded-[12px] border',
          pinned ? 'border-v2-text-secondary' : 'border-transparent',
        )}
      >
        <BadgeMedallion badge={badge} size={72} />
      </span>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-pressed={pinned}
        aria-label={t(pinned ? 'unpin' : 'pin', { title: badge.title })}
        className={cn(
          `absolute -top-2 -right-2 flex size-6 cursor-pointer items-center justify-center
          rounded-[12px] border border-v2-border transition-colors
          focus-visible:outline-2 focus-visible:outline-v2-btn-primary`,
          pinned
            ? 'bg-v2-surface-tertiary text-white'
            : 'bg-v2-surface-secondary text-v2-text-secondary hover:text-white',
          // A full board only stops taking pins; the design keeps them looking
          // the same, so the grey-vs-white of the pin carries the state.
          disabled && 'cursor-not-allowed hover:text-v2-text-secondary',
        )}
      >
        <Icon name="Pin" size={16} />
      </button>
    </li>
  );
}

export function FeatureAchievementsDialog({
  open,
  onOpenChange,
  badges,
}: FeatureAchievementsDialogProps) {
  const t = useTranslations('rewardsHub.achievements');
  const tCommon = useTranslations('common');
  const items = useMemo(() => ownedBadges(badges), [badges]);
  const saved = useMemo(
    () => items.filter((badge) => badge.featured).map((badge) => badge.id),
    [items],
  );

  const [draft, setDraft] = useState<string[]>(saved);
  const { mutate: save, isPending } = useSaveFeaturedBadges();

  // Reopening starts from what is actually pinned, not from the discarded
  // draft of the last visit — and a refetch while open is picked up too.
  useEffect(() => {
    if (open) setDraft(saved);
  }, [open, saved]);

  const handleToggle = useCallback((badgeId: string) => {
    setDraft((prev) =>
      prev.includes(badgeId)
        ? prev.filter((id) => id !== badgeId)
        : prev.length < FEATURED_BADGE_LIMIT
          ? [...prev, badgeId]
          : prev,
    );
  }, []);

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleSave = useCallback(() => {
    save(draft);
    onOpenChange(false);
  }, [draft, onOpenChange, save]);

  const isFull = draft.length >= FEATURED_BADGE_LIMIT;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Figma's 580 px box (13878:195388), six medallions a row. The width is
        set, not left to the content: a centred fixed box only reaches for
        half the viewport, so it shrank to 512 px on a laptop (five a row) and
        265 px on a phone (two). The kit's `sm:max-w-lg` is overridden too.
      */}
      <DialogContent
        showCloseButton={false}
        onOpenAutoFocus={focusDialogOnOpen}
        className="bg-v2-surface-primary border-v2-border max-h-[85vh] w-[580px] max-w-[calc(100vw-2rem)] gap-6
          overflow-y-auto rounded-[20px] p-8 outline-none sm:max-w-[calc(100vw-2rem)]"
      >
        {/* The head as Figma draws it: title and subtitle, a plain cross on the right. */}
        <div className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <DialogTitle className="pr-0 text-[24px] font-semibold leading-8 text-v2-text-primary">
              {t('featureTitle')}
            </DialogTitle>
            <DialogDescription className="text-[14px] text-[#9ea3a8]">
              {t('featureSubtitle', { count: FEATURED_BADGE_LIMIT })}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label={tCommon('close')}
            className="flex size-10 shrink-0 cursor-pointer items-center justify-center text-v2-text-primary
              opacity-80 transition-opacity hover:opacity-100 focus-visible:outline-2 focus-visible:outline-v2-btn-primary"
          >
            <Icon name="Close" size={24} />
          </button>
        </div>

        {items.length === 0 ? (
          <p className="text-[13px] leading-[1.5] text-v2-text-secondary">{t('empty')}</p>
        ) : (
          <ul className="flex flex-wrap content-start items-start gap-x-4 gap-y-5">
            {items.map((badge) => {
              const pinned = draft.includes(badge.id);
              return (
                <PinnableBadge
                  key={badge.id}
                  badge={badge}
                  pinned={pinned}
                  disabled={isFull && !pinned}
                  onToggle={handleToggle}
                />
              );
            })}
          </ul>
        )}

        <div className="flex items-center gap-2">
          <Button appearance="neutral" className="flex-1" onClick={handleClose}>
            {t('featureClose')}
          </Button>
          <Button className="flex-1" onClick={handleSave} disabled={isPending}>
            {t('featureSave')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
